import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import { Trophy, Users, Plus, Award, TrendingUp, Building2, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function CompetitionManager({ games, onRefresh }) {
  const { t } = useTranslation();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`);
      setCompetitions(response.data);
    } catch (err) { console.error("CompetitionManager:", err.message); } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold text-slate-900">Class Competitions</h2>
          <p className="text-sm text-slate-500">Link multiple games for cross-class competition</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-competition-btn">
              <Plus className="w-4 h-4" />
              Create Competition
            </Button>
          </DialogTrigger>
          <DialogContent>
            <CreateCompetitionDialog
              onSuccess={(competition) => {
                setCompetitions([...competitions, competition]);
                setCreateOpen(false);
                toast.success("Competition created!");
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-48 bg-slate-200 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : competitions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Competitions Yet</h3>
            <p className="text-slate-500 max-w-sm mx-auto">
              Create a competition to link multiple class sections and let students compete across games!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {competitions.map((competition) => (
            <CompetitionCard
              key={competition.id}
              competition={competition}
              games={games}
              onRefresh={fetchCompetitions}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CompetitionCard({ competition, games, onRefresh }) {
  const { t } = useTranslation();
  const [addGameOpen, setAddGameOpen] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/competitions/${competition.id}`);
      toast.success(t("competition.deleted"));
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete competition");
    } finally {
      setDeleting(false);
    }
  };

  const viewLeaderboard = async () => {
    setLoadingLeaderboard(true);
    try {
      const response = await axios.get(`${API}/competitions/${competition.id}/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      toast.error("Failed to load leaderboard");
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const addGameToCompetition = async (gameId) => {
    try {
      await axios.post(`${API}/competitions/${competition.id}/games/${gameId}`);
      toast.success("Game added to competition!");
      setAddGameOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add game");
    }
  };

  // Available games not yet in competition
  const availableGames = games.filter(g => !competition.games?.includes(g.id));

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <CardTitle className="font-heading">{competition.name}</CardTitle>
              <CardDescription>{competition.description || "No description"}</CardDescription>
            </div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50"
                data-testid={`delete-competition-${competition.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="w-5 h-5" />
                  {t("competition.deleteConfirm")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t("competition.deleteWarning", { name: competition.name })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting}
                  data-testid={`confirm-delete-competition-${competition.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleting ? t("instructor.deleting") : t("competition.delete")}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1 text-slate-600">
            <Building2 className="w-4 h-4" />
            <span>{competition.games?.length || 0} games</span>
          </div>
        </div>

        {/* Games in competition */}
        {competition.games?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Participating Games</p>
            <div className="flex flex-wrap gap-2">
              {competition.games.map((gameId) => {
                const game = games.find(g => g.id === gameId);
                return (
                  <Badge key={gameId} variant="secondary">
                    {game?.name || "Unknown Game"}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Dialog open={addGameOpen} onOpenChange={setAddGameOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1" disabled={availableGames.length === 0}>
                <Plus className="w-3 h-3" />
                Add Game
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Game to Competition</DialogTitle>
                <DialogDescription>Select a game to add to {competition.name}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                {availableGames.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">All games are already in this competition</p>
                ) : (
                  availableGames.map((game) => (
                    <button
                      key={game.id}
                      onClick={() => addGameToCompetition(game.id)}
                      className="w-full p-4 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <p className="font-medium">{game.name}</p>
                      <p className="text-sm text-slate-500">Q{game.current_quarter} • {game.status}</p>
                    </button>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Button size="sm" onClick={viewLeaderboard} disabled={loadingLeaderboard} className="gap-1">
            <Award className="w-3 h-3" />
            {loadingLeaderboard ? "Loading..." : "View Leaderboard"}
          </Button>
        </div>

        {/* Leaderboard Preview */}
        {leaderboard && (
          <div className="mt-4 pt-4 border-t border-amber-200">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-700">Competition Standings</p>
              <Badge variant="outline">{leaderboard.total_teams} teams</Badge>
            </div>

            {/* Game Rankings */}
            <div className="space-y-2 mb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Class Rankings</p>
              {leaderboard.game_rankings?.slice(0, 3).map((game, index) => (
                <div key={game.game_id} className="flex items-center justify-between p-2 bg-white rounded border border-slate-100">
                  <div className="flex items-center gap-2">
                    <RankBadge rank={game.rank} />
                    <span className="font-medium text-slate-800">{game.game_name}</span>
                  </div>
                  <span className="text-sm text-slate-600">Avg: {formatCurrency(game.avg_profit)}</span>
                </div>
              ))}
            </div>

            {/* Top Teams */}
            <div className="space-y-2">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Top Teams Overall</p>
              {leaderboard.team_leaderboard?.slice(0, 5).map((team) => (
                <div key={team.team_id} className="flex items-center justify-between p-2 bg-white rounded border border-slate-100">
                  <div className="flex items-center gap-2">
                    <RankBadge rank={team.rank} />
                    <div>
                      <span className="font-medium text-slate-800">{team.name}</span>
                      <span className="text-xs text-slate-400 ml-2">({team.game_name})</span>
                    </div>
                  </div>
                  <span className={`text-sm font-mono ${team.cumulative_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {formatCurrency(team.cumulative_profit)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CreateCompetitionDialog({ onSuccess }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(`${API}/competitions`, { name, description });
      onSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create competition");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create Class Competition</DialogTitle>
        <DialogDescription>
          Create a competition to link multiple class sections together
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleCreate} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label htmlFor="compName">Competition Name</Label>
          <Input
            id="compName"
            placeholder="MBA Strategy Cup 2025"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="competition-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="compDesc">Description (optional)</Label>
          <Textarea
            id="compDesc"
            placeholder="Cross-section competition for MBA Strategy classes..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            data-testid="competition-description-input"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="submit" disabled={creating} data-testid="create-competition-submit">
            {creating ? "Creating..." : "Create Competition"}
          </Button>
        </div>
      </form>
    </>
  );
}

function RankBadge({ rank }) {
  const colors = {
    1: "bg-amber-400 text-amber-900",
    2: "bg-slate-300 text-slate-700",
    3: "bg-amber-600 text-white"
  };

  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${colors[rank] || "bg-slate-100 text-slate-600"}`}>
      {rank}
    </span>
  );
}

export default CompetitionManager;
